"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OVER_BUDGET = exports.EXPERIENCE_BANDS = exports.NOT_GIVEN = exports.RANKING_FILTER_LABELS = exports.RANKING_FILTER_KEYS = void 0;
exports.encodeFilterValues = encodeFilterValues;
exports.decodeFilterValues = decodeFilterValues;
exports.experienceBandOf = experienceBandOf;
exports.cvTypeOf = cvTypeOf;
exports.reviewValueOf = reviewValueOf;
exports.rankingFilterValuesOf = rankingFilterValuesOf;
exports.rankingFilterLabel = rankingFilterLabel;
exports.matchesRankingFilters = matchesRankingFilters;
exports.rankingFilterOptions = rankingFilterOptions;
exports.hasRankingFilters = hasRankingFilters;
exports.activeRankingFilters = activeRankingFilters;
const candidates_1 = require("./candidates");
const enums_1 = require("./enums");
const publishing_1 = require("./publishing");
const remarks_1 = require("./remarks");
const shortlist_1 = require("./shortlist");
// RNK-01's filters (F23.11, ADR 0049, DECISIONS-LOG 2026-10-17 "Ranking 2.0 Part 1"). Like keywords (ADR 0048)
// they only narrow who is shown: a score, a weight or a tab never moves. Several values in one filter mean
// either; different filters mean all. Every live row is already in the browser, so this is worked out there.
exports.RANKING_FILTER_KEYS = ['rloc', 'rexp', 'rdeg', 'rcv', 'rrmk', 'rrev', 'rsrc', 'rsal'];
exports.RANKING_FILTER_LABELS = {
    rloc: 'Location',
    rexp: 'Experience',
    rdeg: 'Degree',
    rcv: 'CV type',
    rrmk: 'Remarks',
    rrev: 'Review status',
    rsrc: 'Source',
    rsal: 'Salary',
};
/** Values in the address are joined by "|", because a city as typed may hold a comma ("Mirpur, Dhaka"). */
const SEPARATOR = '|';
const VALUE_MAX = 120;
function encodeFilterValues(values) {
    const kept = values.filter((value) => value !== '' && !value.includes(SEPARATOR));
    return kept.length > 0 ? kept.join(SEPARATOR) : undefined;
}
function decodeFilterValues(value) {
    if (!value)
        return [];
    return [...new Set(value.split(SEPARATOR).filter((part) => part !== '' && part.length <= VALUE_MAX))];
}
/** A value the candidate didn't give: no city, no experience, no degree. */
exports.NOT_GIVEN = 'none';
const NOT_GIVEN_LABEL = 'Not given';
/** Under 2 years, 2–4, 4–7, 7+ (ADR 0049); each band holds its lower end, so exactly 4 years is "4–7". */
exports.EXPERIENCE_BANDS = [
    { value: 'lt2', label: 'Under 2 years', belowMonths: 24 },
    { value: '2to4', label: '2–4 years', belowMonths: 48 },
    { value: '4to7', label: '4–7 years', belowMonths: 84 },
    { value: '7plus', label: '7+ years', belowMonths: Number.POSITIVE_INFINITY },
];
function experienceBandOf(months) {
    if (months === null)
        return exports.NOT_GIVEN;
    return exports.EXPERIENCE_BANDS.find((band) => months < band.belowMonths)?.value ?? exports.NOT_GIVEN;
}
const CV_TYPE_LABELS = {
    pdf: 'PDF',
    word: 'Word',
    unreadable: "Couldn't be read",
    coming: 'CV coming',
    none: 'No CV',
};
function cvTypeOf(row) {
    if (row.cv === 'ready')
        return row.cvKind === 'word' ? 'word' : 'pdf';
    return row.cv;
}
/** Past review: moved on from ③, whatever HR's review said. */
const PAST_REVIEW = 'past';
function reviewValueOf(row) {
    if (row.stage !== 'screening')
        return PAST_REVIEW;
    return row.reviewStatus ?? 'to_review';
}
const ANY_REMARK = 'any';
/**
 * DECISIONS-LOG 2026-10-19 Group 3 Q3: Salary has one value. The percent is masked to null for a viewer who may
 * not see `candidate.expectedSalary` (#5), so for them nobody matches; the screen doesn't offer the filter at all.
 */
exports.OVER_BUDGET = 'over';
/** The values a row has for a filter; a person can carry several remark tags. */
function rankingFilterValuesOf(row, key) {
    switch (key) {
        case 'rloc':
            return [row.city?.trim() || exports.NOT_GIVEN];
        case 'rexp':
            return [experienceBandOf(row.experienceMonths)];
        case 'rdeg':
            return [row.education ?? exports.NOT_GIVEN];
        case 'rcv':
            return [cvTypeOf(row)];
        case 'rrmk':
            return row.remarks.count > 0 ? [ANY_REMARK, ...row.remarks.tags] : [exports.NOT_GIVEN];
        case 'rrev':
            return [reviewValueOf(row)];
        case 'rsrc':
            return [row.source ?? publishing_1.DIRECT_SOURCE];
        case 'rsal':
            return row.application.expectedSalaryOverBudgetPercent === null ? [] : [exports.OVER_BUDGET];
    }
}
function rankingFilterLabel(key, value) {
    switch (key) {
        case 'rloc':
            return value === exports.NOT_GIVEN ? NOT_GIVEN_LABEL : value;
        case 'rexp':
            return exports.EXPERIENCE_BANDS.find((band) => band.value === value)?.label ?? NOT_GIVEN_LABEL;
        case 'rdeg':
            return isEducationLevel(value) ? enums_1.EDUCATION_LEVEL_LABELS[value] : NOT_GIVEN_LABEL;
        case 'rcv':
            return CV_TYPE_LABELS[value] ?? value;
        case 'rrmk':
            if (value === ANY_REMARK)
                return 'Has a remark';
            if (value === exports.NOT_GIVEN)
                return 'No remark';
            return isRemarkTag(value) ? remarks_1.REMARK_TAG_LABELS[value] : value;
        case 'rrev':
            if (value === PAST_REVIEW)
                return 'Moved on';
            return value === 'to_review' || value === 'maybe' || value === 'shortlisted' ? shortlist_1.REVIEW_STATUS_LABELS[value] : value;
        case 'rsrc':
            return (0, candidates_1.applicationSourceLabel)(value);
        case 'rsal':
            return value === exports.OVER_BUDGET ? 'Over budget' : value;
    }
}
function isEducationLevel(value) {
    return enums_1.EDUCATION_LEVELS.some((level) => level === value);
}
function isRemarkTag(value) {
    return remarks_1.REMARK_TAGS.some((tag) => tag === value);
}
/** The fixed order of a filter's values; Location and Source go by count instead. */
const FIXED_ORDER = {
    rexp: [...exports.EXPERIENCE_BANDS.map((band) => band.value), exports.NOT_GIVEN],
    rdeg: [...enums_1.EDUCATION_LEVELS, exports.NOT_GIVEN],
    rcv: ['pdf', 'word', 'unreadable', 'coming', 'none'],
    rrmk: [ANY_REMARK, exports.NOT_GIVEN, ...remarks_1.REMARK_TAGS],
    rrev: ['to_review', 'maybe', 'shortlisted', PAST_REVIEW],
    rsal: [exports.OVER_BUDGET],
};
/** Values listed even at 0, so "Over budget 0" says nobody asks above the budget rather than an empty pop-over. */
const ALWAYS_LISTED = { rsal: [exports.OVER_BUDGET] };
/** Does the row pass every filter — leaving one out when counting that filter's own values. */
function matchesRankingFilters(row, filters, except) {
    return exports.RANKING_FILTER_KEYS.every((key) => {
        const selected = filters[key];
        if (key === except || !selected || selected.length === 0)
            return true;
        return rankingFilterValuesOf(row, key).some((value) => selected.includes(value));
    });
}
/** A filter's values with their counts, standard faceting: a selected value stays listed even at 0. */
function rankingFilterOptions(rows, filters, key) {
    const selected = filters[key] ?? [];
    const counts = new Map();
    for (const row of rows) {
        if (!matchesRankingFilters(row, filters, key))
            continue;
        for (const value of rankingFilterValuesOf(row, key))
            counts.set(value, (counts.get(value) ?? 0) + 1);
    }
    for (const value of [...selected, ...(ALWAYS_LISTED[key] ?? [])])
        if (!counts.has(value))
            counts.set(value, 0);
    const options = [...counts].map(([value, count]) => ({ value, label: rankingFilterLabel(key, value), count, isSelected: selected.includes(value) }));
    const order = FIXED_ORDER[key];
    if (order)
        return options.sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value));
    return options.sort((a, b) => (a.value === exports.NOT_GIVEN ? 1 : 0) - (b.value === exports.NOT_GIVEN ? 1 : 0) || b.count - a.count || a.label.localeCompare(b.label));
}
function hasRankingFilters(filters) {
    return exports.RANKING_FILTER_KEYS.some((key) => (filters[key]?.length ?? 0) > 0);
}
/** The active filters as chips, in filter order. */
function activeRankingFilters(filters) {
    return exports.RANKING_FILTER_KEYS.flatMap((key) => (filters[key] ?? []).map((value) => ({ key, value, label: rankingFilterLabel(key, value) })));
}
//# sourceMappingURL=ranking-filters.js.map